import { useEffect, useState, useCallback } from "react";
import { TrendingUp, Plus, Trash2, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINRFull, formatDate } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const empty = { category: "Material", description: "", amount: "", date: "" };

export function ProfitabilityPanel({ projectId, onChanged }) {
  const { meta } = useAuth();
  const [costs, setCosts] = useState([]);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        api.get(`/projects/${projectId}/costs`),
        api.get(`/projects/${projectId}/financials`),
      ]);
      setCosts(c.data); setFin(f.data);
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...empty, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amount = Number(form.amount) || 0;
    if (amount <= 0) { toast.error("Cost amount must be greater than 0"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/costs`, { category: form.category, description: form.description || null, amount, date: form.date });
      toast.success("Cost added");
      setOpen(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => { await api.delete(`/costs/${del.id}`); toast.success("Cost deleted"); setDel(null); load(); onChanged && onChanged(); };

  if (loading || !fin) return <div className="h-40 animate-pulse rounded-xl bg-slate-100" />;

  const profitColor = fin.gross_profit >= 0 ? "#059669" : "#DC2626";

  return (
    <div className="space-y-4" data-testid="profitability-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Invoiced (Revenue)" value={formatINRFull(fin.invoiced_taxable)} color="#0057A8" />
        <Metric label="Total Cost" value={formatINRFull(fin.cost_total)} color="#EA580C" />
        <Metric label="Gross Profit" value={formatINRFull(fin.gross_profit)} color={profitColor} testid="profit-value" />
        <Metric label="Gross Margin" value={`${fin.margin_pct}%`} color={profitColor} testid="margin-value" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Revenue vs Cost</h3>
        <div className="space-y-2 text-sm">
          <Row label="Quoted Value" value={formatINRFull(fin.quoted_value)} />
          <Row label="Invoiced (excl. GST)" value={formatINRFull(fin.invoiced_taxable)} />
          <Row label="Received" value={formatINRFull(fin.received_total)} />
          <Row label="Balance Due" value={formatINRFull(fin.balance_due)} />
          <div className="my-2 border-t border-slate-100" />
          {Object.entries(fin.cost_breakdown || {}).map(([cat, amt]) => (
            <Row key={cat} label={`Cost · ${cat}`} value={formatINRFull(amt)} muted />
          ))}
          <Row label="Total Cost" value={formatINRFull(fin.cost_total)} strong />
          <div className="my-2 border-t border-slate-100" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Gross Profit</span>
            <span className="font-mono text-lg font-extrabold" style={{ color: profitColor }}>{formatINRFull(fin.gross_profit)} · {fin.margin_pct}%</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-[#0057A8]" /><h3 className="text-base font-semibold text-slate-800">Project Costs</h3></div>
        <Button data-testid="add-cost-btn" onClick={openNew} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Add Cost</Button>
      </div>

      {costs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-slate-700">No costs recorded yet</p>
          <p className="mt-1 text-xs text-slate-400">Add material, labour and overhead costs to see this project's true margin.</p>
          <Button onClick={openNew} className="mt-4 gap-1.5"><Plus className="h-4 w-4" /> Add First Cost</Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Category</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {costs.map((c) => (
                <tr key={c.id} data-testid={`cost-row-${c.id}`} className="border-b border-slate-50">
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{c.category}</span></td>
                  <td className="px-4 py-3 text-slate-600">{c.description || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(c.date)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">{formatINRFull(c.amount)}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => setDel(c)} data-testid={`delete-cost-${c.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-slate-500">Total Cost</td><td className="px-4 py-3 text-right font-mono text-base font-extrabold text-slate-900">{formatINRFull(fin.cost_total)}</td><td></td></tr></tfoot>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Project Cost</DialogTitle><DialogDescription>Log a material, labour or overhead cost to track this project's true margin.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="cost-category"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.cost_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Amount (₹)</Label><Input data-testid="cost-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Date</Label><Input data-testid="cost-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Description</Label><Input data-testid="cost-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Epoxy resin — 200 kg" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="cost-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : "Add Cost"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Delete cost?" description="This removes the cost entry." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}

function Metric({ label, value, color = "#102A43", testid }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 font-mono text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function Row({ label, value, muted, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-slate-400" : "text-slate-500"}>{label}</span>
      <span className={`font-mono ${strong ? "font-semibold text-slate-800" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}
